import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabaseRpc } from '@/lib/supabaseClient'

export type DigestMode = 'immediate' | 'daily' | 'off'

export interface NotificationPreferences {
  id: string
  user_id: string
  email_enabled: boolean
  sms_enabled: boolean
  in_app_enabled: boolean
  digest_mode: DigestMode
}

export const DEFAULT_NOTIFICATION_PREFERENCES: Omit<
  NotificationPreferences,
  'id' | 'user_id'
> = {
  email_enabled: true,
  sms_enabled: false,
  in_app_enabled: true,
  digest_mode: 'immediate',
}

export interface PreferencesQueryResult {
  userId: string | null
  preferences: NotificationPreferences | null
}

type PreferencesRpcRow = {
  user_id: string | null
  preferences: NotificationPreferences | null
}

export function useNotificationPreferencesQuery() {
  return useQuery<PreferencesQueryResult>({
    queryKey: ['notification-preferences'],
    queryFn: async () => {
      const data = await supabaseRpc<PreferencesRpcRow[] | PreferencesRpcRow | null>('get_my_notification_preferences')
      const row = Array.isArray(data) ? data[0] ?? null : data
      return {
        userId: row?.user_id ?? null,
        preferences: row?.preferences ?? null,
      }
    },
  })
}

export interface UpsertPreferencesParams {
  userId: string
  emailEnabled: boolean
  smsEnabled: boolean
  inAppEnabled: boolean
  digestMode: DigestMode
}

export function useUpsertNotificationPreferencesMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: UpsertPreferencesParams) =>
      supabaseRpc<NotificationPreferences>('upsert_my_notification_preferences', {
        p_email_enabled: params.emailEnabled,
        p_sms_enabled: params.smsEnabled,
        p_in_app_enabled: params.inAppEnabled,
        p_digest_mode: params.digestMode,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notification-preferences'] })
    },
  })
}
